<template>
  <footer class="footer-area style-three bg-title pt-115">
    <div class="container">
      <div class="row pb-85">
        <!-- Logo & Newsletter -->
        <div class="col-xl-3 col-md-6">
          <div class="footer-widget mb-30">
            <router-link to="/" class="logo d-block mb-22">
              <img :src="logoLight" alt="Logo" />
            </router-link>
            <h3 class="fs-20 fw-semibold text_secondary">Newsletter</h3>
            <p class="mb-18">Subscribe to our newsletter for the latest updates and news</p>
            <form @submit.prevent="submitNewsletter" class="newsletter-form style-two position-relative">
              <input
                v-model="newsletterEmail"
                type="email"
                class="w-100 bg-transparent text-white outline-0"
                placeholder="Email address"
              />
              <button 
                type="submit"
                class="bg-transparent border-0 position-absolute text_secondary end-0"
                :disabled="subscribing"
              >
                <i class="ri-send-plane-2-line"></i>
              </button>
            </form>
          </div>
        </div>
        
        <!-- Contact Info -->
        <div class="col-xl-3 offset-xl-1 col-md-6">
          <div class="footer-widget mb-30">
            <ul class="contact-info list-unstyled mb-0">
              <li v-if="contactPhone" class="mb-20">
                <span class="d-block mb-1 text-white">Call Us</span>
                <a
                  :href="`tel:${contactPhone}`"
                  class="text_secondary fw-bold hover-text-primary transition"
                >{{ contactPhone }}</a>
              </li>
              <li v-if="contactEmail" class="mb-20">
                <span class="d-block mb-1 text-white">Email Us</span>
                <a
                  :href="`mailto:${contactEmail}`"
                  class="text_secondary fw-semibold hover-text-primary transition"
                >{{ contactEmail }}</a>
              </li>
              <li v-if="address">
                <span class="d-block mb-1 text-white">Address</span>
                <p class="mb-0 pe-xxl-5">{{ address }}</p>
              </li>
            </ul>
          </div>
        </div>
        
        <!-- Pages Links -->
        <div class="col-xl-3 col-md-6">
          <div class="footer-widget mb-30">
            <h3 class="footer-widget-title fs-20 fw-semibold text_secondary mb-30">
              Pages
            </h3>
            <ul class="footer-menu style-two list-unstyled mb-0">
              <li v-for="page in pages.slice(0, 6)" :key="page.id">
                <router-link :to="`/pages/${page.slug}`">{{ page.title }}</router-link>
              </li>
            </ul>
          </div>
        </div>
        
        <!-- Quick Links -->
        <div class="col-xl-2 col-md-6">
          <div class="footer-widget mb-30">
            <h3 class="footer-widget-title fs-20 fw-semibold text_secondary mb-30">
              Quick Links
            </h3>
            <ul class="footer-menu style-two list-unstyled mb-0">
              <li>
                <router-link to="/">Home</router-link>
              </li>
              <li v-for="page in pages.slice(6, 10)" :key="page.id">
                <router-link :to="`/pages/${page.slug}`">{{ page.title }}</router-link>
              </li>
              <li>
                <router-link to="/contact">Contact Us</router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Footer Bottom -->
    <div class="footer-bottom">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-md-7 pe-md-0 mb-sm-10">
            <p class="copyright-text text-offwhite text-md-start text-center mb-0">
              <i class="ri-copyright-line"></i>
              <span class="text-white fw-semibold ms-1 me-1">{{ siteName }}</span>
              {{ new Date().getFullYear() }}. All rights reserved.
            </p>
          </div>
          <div class="col-md-5 text-md-end text-center">
            <ul class="social-profile style-two list-unstyled mb-0" v-if="socialLinks.length">
              <li v-for="(social, index) in socialLinks" :key="index">
                <a
                  :href="social.link"
                  target="_blank"
                  class="d-flex flex-column align-items-center justify-content-center rounded-circle"
                >
                  <i :class="social.icon"></i>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { useSiteDefaultsStore } from '@/stores/siteDefaultsStore'

export default {
    name: 'AppFooter',
    data() {
        return {
            pages: [],
            newsletterEmail: '',
            subscribing: false
        }
    },
    computed: {
        siteDefaultsStore() {
            return useSiteDefaultsStore()
        },
        logoLight() {
            return this.siteDefaultsStore.getLogoLight() || '/assets/img/logo-white.png'
        },
        siteName() {
            return this.siteDefaultsStore.getSiteName() || 'Olavo'
        },
        contactEmail() {
            return this.siteDefaultsStore.getContactEmail() || ''
        },
        contactPhone() {
            return this.siteDefaultsStore.getContactPhone() || ''
        },
        address() {
            return this.siteDefaultsStore.getAddress() || ''
        },
        socialLinks() {
            return this.siteDefaultsStore.getSocialLinks() || []
        }
    },
    async mounted() {
        // Load pages for footer links
        await this.loadPages()
    },
    methods: {
        async loadPages() {
            try {
                const response = await this.$api.get('public/pages/list')
                if (response.data && response.data.status === 'success') {
                    this.pages = response.data.data.sort((a, b) => a.sort_order - b.sort_order)
                }
            } catch (error) {
                console.error('Failed to load pages:', error)
                this.pages = []
            }
        },
        
        async submitNewsletter() {
            if (!this.newsletterEmail.trim()) return
            
            this.subscribing = true
            
            try {
                // Newsletter subscription API call
                const response = await this.$api.post('public/newsletter/subscribe', {
                    email: this.newsletterEmail
                })
                
                if (response.data && response.data.status === 'success') {
                    this.$toast.success('Thank you for subscribing to our newsletter!', 4000)
                    this.newsletterEmail = ''
                }
            } catch (error) {
                console.error('Newsletter subscription failed:', error)
                this.$toast.error('Failed to subscribe. Please try again later.', 4000)
            } finally {
                this.subscribing = false
            }
        }
    }
}
</script>

<style scoped>
.footer-area.style-three {
    background-color: #303E4D;
}

.footer-area.style-three p {
    color: rgba(255, 255, 255, 0.7);
}

.footer-area.style-three .newsletter-form.style-two {
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    padding-bottom: 10px;
}

.footer-area.style-three .newsletter-form.style-two input {
    padding: 10px 0;
    border: none;
}

.footer-area.style-three .newsletter-form.style-two input::placeholder {
    color: rgba(255, 255, 255, 0.5);
}

.footer-area.style-three .newsletter-form.style-two button {
    font-size: 20px;
}

.footer-area.style-three .contact-info li {
    color: rgba(255, 255, 255, 0.7);
}

.footer-area.style-three .footer-menu.style-two li {
    margin-bottom: 12px;
}

.footer-area.style-three .footer-menu.style-two li a {
    color: rgba(255, 255, 255, 0.7);
    transition: color 0.3s ease;
    text-decoration: none;
}

.footer-area.style-three .footer-menu.style-two li a:hover {
    color: #6698C8;
}

.footer-area.style-three .footer-bottom {
    padding: 30px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-area.style-three .social-profile.style-two li {
    display: inline-block;
    margin-left: 10px;
}

.footer-area.style-three .social-profile.style-two li a {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
}

.footer-area.style-three .social-profile.style-two li a:hover {
    background: #6698C8;
    transform: translateY(-3px);
}

.footer-area.style-three .social-profile.style-two li a i {
    color: white;
}

.text-offwhite {
    color: rgba(255, 255, 255, 0.7);
}
</style>
